/**
 * Google Identity Services (One Tap / FedCM). The GIS script loads on first signIn(). The credential is an OIDC id
 * token whose audience is the web OAuth client id.
 *
 * Replay protection: each sign-in fetches a fresh server nonce (GET /v1/auth/nonce, `nonce` below) while the script
 * loads, and GIS puts it into the token as is (`initialize({ nonce })`). The raw nonce goes to the API with the token.
 */
import { loadScript } from '../lazy.js';
import { SignInCancelled, type IdentityProvider, type IdentityToken } from './types.js';

interface PromptMoment {
  isNotDisplayed?(): boolean;
  isSkippedMoment?(): boolean;
  isDismissedMoment?(): boolean;
  getDismissedReason?(): string;
}
interface GoogleId {
  initialize(o: { client_id: string; callback: (r: { credential: string }) => void; nonce?: string; use_fedcm_for_prompt?: boolean; itp_support?: boolean; cancel_on_tap_outside?: boolean; auto_select?: boolean }): void;
  prompt(listener?: (m: PromptMoment) => void): void;
}

export interface GoogleSignInConfig {
  /** OAuth 2.0 web client id, …apps.googleusercontent.com */
  clientId: string;
  document?: Document;
  /** a fresh single-use server nonce for each sign-in (fetchAuthNonce; createProviders sets it); null signs in without one */
  nonce?: () => Promise<string | null>;
}

export class GoogleIdentityProvider implements IdentityProvider {
  readonly provider = 'google';

  constructor(private readonly c: GoogleSignInConfig) {}

  async signIn(): Promise<IdentityToken> {
    const [, nonce] = await Promise.all([loadScript('https://accounts.google.com/gsi/client', this.c.document), this.c.nonce?.() ?? null]);
    const id = (window as unknown as { google: { accounts: { id: GoogleId } } }).google.accounts.id;
    return new Promise<IdentityToken>((resolve, reject) => {
      id.initialize({
        client_id: this.c.clientId,
        callback: (r) => resolve({ provider: 'google', idToken: r.credential, ...(nonce ? { nonce } : {}) }),
        ...(nonce ? { nonce } : {}),
        use_fedcm_for_prompt: true,
        // Safari / Firefox (ITP): without this One Tap is not displayed and sign-in reads as cancelled
        itp_support: true,
        cancel_on_tap_outside: true,
      });
      id.prompt((m) => {
        if (m.isNotDisplayed?.() || m.isSkippedMoment?.() || (m.isDismissedMoment?.() && m.getDismissedReason?.() !== 'credential_returned')) {
          reject(new SignInCancelled('google'));
        }
      });
    });
  }
}
